import React, { useMemo, useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Stack,
  TextField,
  MenuItem,
  Switch,
  FormControlLabel,
  Slider,
  Button,
  Divider,
} from "@mui/material";

/**
 * AppearanceSetting
 * Configure chat appearance (theme, colors, bubbles, typography, avatars) with a live preview.
 *
 * Props:
 * - initial: optional initial values
 * - onSave: (payload) => Promise<void> | void
 * - onCancel: () => void
 */
export default function AppearanceSetting({ initial, onSave, onCancel }) {
  const defaults = useMemo(
    () => ({
      themeMode: "light", // 'light' | 'dark'
      primary: "#2563eb",
      bg: "#ffffff",
      bubbleGuest: "#f3f4f6",
      bubbleStaff: "#2563eb",
      textGuest: "#111827",
      textStaff: "#ffffff",
      borderRadius: 12,
      messageRadius: 16,
      fontSize: 14,
      showTimestamps: true,
      showAvatars: true,
      avatarShape: "circle", // 'circle' | 'rounded' | 'square'
      logoUrl: "",
      agentName: "Concierge Bot",
      ...(initial || {}),
    }),
    [initial]
  );

  const [form, setForm] = useState(defaults);
  const [saving, setSaving] = useState(false);

  useEffect(() => setForm(defaults), [defaults]);

  const handle = (key) => (e, val) => {
    const v = typeof val === "number" || typeof val === "string" ? val : e?.target?.value;
    const isChecked = e?.target?.type === "checkbox" ? !!e.target.checked : undefined;
    setForm((f) => ({ ...f, [key]: isChecked ?? v }));
  };

  const handleColor = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave?.(form);
    } finally {
      setSaving(false);
    }
  };

  const Bubble = ({ me, children }) => (
    <Box
      sx={{
        px: 1.5,
        py: 1,
        maxWidth: 320,
        alignSelf: me ? "flex-end" : "flex-start",
        color: me ? form.textStaff : form.textGuest,
        bgcolor: me ? form.bubbleStaff : form.bubbleGuest,
        borderRadius: form.messageRadius,
        fontSize: form.fontSize,
        boxShadow: 0,
      }}
    >
      {children}
    </Box>
  );

  const Avatar = ({ me }) => (
    <Box
      sx={{
        width: 28,
        height: 28,
        bgcolor: me ? form.primary : "#e5e7eb",
        borderRadius:
          form.avatarShape === "circle"
            ? "50%"
            : form.avatarShape === "rounded"
            ? 8
            : 2,
        mr: 1,
      }}
    />
  );

  const Preview = () => (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        bgcolor: form.themeMode === "dark" ? "#0b1220" : form.bg,
        borderRadius: form.borderRadius,
        minHeight: 260,
      }}
    >
      <Typography variant="subtitle2" sx={{ mb: 1 }}>Preview</Typography>
      <Stack spacing={1}>
        <Stack direction="row" alignItems="flex-end">
          {form.showAvatars && <Avatar me={false} />}
          <Bubble me={false}>Hello, I’d like to book a spa treatment.</Bubble>
        </Stack>
        {form.showTimestamps && (
          <Typography variant="caption" color="text.secondary" sx={{ ml: form.showAvatars ? 4.5 : 0 }}>
            10:12
          </Typography>
        )}
        <Stack direction="row" alignItems="flex-end" justifyContent="flex-end">
          <Bubble me>Sure! What time works best for you?</Bubble>
          {form.showAvatars && <Avatar me />}
        </Stack>
        {form.showTimestamps && (
          <Typography variant="caption" color="text.secondary" sx={{ textAlign: "right", mr: form.showAvatars ? 4.5 : 0 }}>
            10:13
          </Typography>
        )}
      </Stack>
    </Paper>
  );

  return (
    <Box sx={{ p: 3, height: "100%", overflow: "auto" }}>
      <Typography variant="h6" sx={{ mb: 1 }}>🎨 Appearance</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Customize the look and feel of the chat room. Use the live preview to see changes.
      </Typography>

      <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
        {/* Controls */}
        <Paper variant="outlined" sx={{ p: 2, minWidth: 360 }}>
          <Stack spacing={2}>
            <Stack direction="row" spacing={2}>
              <TextField
                select
                size="small"
                label="Theme Mode"
                value={form.themeMode}
                onChange={handle("themeMode")}
                sx={{ minWidth: 160 }}
              >
                <MenuItem value="light">Light</MenuItem>
                <MenuItem value="dark">Dark</MenuItem>
              </TextField>
              <TextField
                size="small"
                label="Primary Color"
                type="color"
                value={form.primary}
                onChange={handleColor("primary")}
                sx={{ width: 130 }}
                InputLabelProps={{ shrink: true }}
              />
            </Stack>

            <Divider />
            <Typography variant="subtitle2">Chat Bubbles</Typography>
            <Stack direction="row" spacing={2}>
              <TextField
                size="small"
                label="Guest Bubble"
                type="color"
                value={form.bubbleGuest}
                onChange={handleColor("bubbleGuest")}
                sx={{ width: 130 }}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                size="small"
                label="Guest Text"
                type="color"
                value={form.textGuest}
                onChange={handleColor("textGuest")}
                sx={{ width: 130 }}
                InputLabelProps={{ shrink: true }}
              />
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField
                size="small"
                label="Staff Bubble"
                type="color"
                value={form.bubbleStaff}
                onChange={handleColor("bubbleStaff")}
                sx={{ width: 130 }}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                size="small"
                label="Staff Text"
                type="color"
                value={form.textStaff}
                onChange={handleColor("textStaff")}
                sx={{ width: 130 }}
                InputLabelProps={{ shrink: true }}
              />
            </Stack>

            <Divider />
            <Typography variant="subtitle2">Radii & Typography</Typography>
            <Stack spacing={1}>
              <Typography variant="caption">Container radius: {form.borderRadius}px</Typography>
              <Slider value={form.borderRadius} onChange={handle("borderRadius")} step={1} min={0} max={24} />
              <Typography variant="caption">Bubble radius: {form.messageRadius}px</Typography>
              <Slider value={form.messageRadius} onChange={handle("messageRadius")} step={1} min={0} max={24} />
              <Typography variant="caption">Font size: {form.fontSize}px</Typography>
              <Slider value={form.fontSize} onChange={handle("fontSize")} step={1} min={12} max={18} />
            </Stack>

            <Divider />
            <Typography variant="subtitle2">Avatars & Meta</Typography>
            <Stack direction="row" spacing={2}>
              <TextField
                select
                size="small"
                label="Avatar shape"
                value={form.avatarShape}
                onChange={handle("avatarShape")}
                sx={{ minWidth: 160 }}
              >
                <MenuItem value="circle">Circle</MenuItem>
                <MenuItem value="rounded">Rounded</MenuItem>
                <MenuItem value="square">Square</MenuItem>
              </TextField>
              <TextField
                size="small"
                label="Logo URL"
                value={form.logoUrl}
                onChange={handle("logoUrl")}
                sx={{ minWidth: 220 }}
              />
            </Stack>
            <FormControlLabel
              control={<Switch checked={!!form.showAvatars} onChange={handle("showAvatars")} />}
              label="Show avatars"
            />
            <FormControlLabel
              control={<Switch checked={!!form.showTimestamps} onChange={handle("showTimestamps")} />}
              label="Show timestamps"
            />

            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button onClick={() => { setForm(defaults); onCancel?.(); }}>Cancel</Button>
              <Button variant="contained" onClick={handleSave} disabled={saving}>
                {saving ? "Saving..." : "Save"}
              </Button>
            </Stack>
          </Stack>
        </Paper>

        {/* Preview */}
        <Box sx={{ flexGrow: 1 }}>
          <Preview />
        </Box>
      </Stack>
    </Box>
  );
}
